import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import debounce from "debounce";
import {
  setManualMeshingSelectedSensors,
  isResourceInState,
} from "../../redux/sensorAnalysis";
import {
  DISTANCE_FROM_WINDOW_CENTER,
  SPHERE_RADIUS,
  SNAP_HANDLE_RADIUS,
  SNAP_HANDLE_OFFSET,
  DEBOUNCE_TIME,
  worldToWindowLocal,
  windowLocalToWorld,
  getWindowLocalCoordSystem,
  getAllMirrorWindowSensors as getAllMirrorWindowNewSensors,
  calculateSnapLines,
  getSnapPosition,
  isSnapTooClose,
  getWindowCenter,
} from "./utils";
import { windowEvents } from "../events";
import { useEventSubscription, useManualMeshSensors } from "../CustomHooks";
import { isEventAClick } from "../utils";
import { WindowClickDetector } from "./WindowClickDetector";
import { useWindowSelection } from "../objectSelection";
import MeshWindow from "./MeshWindow";
import SnapLines from "./SnapLines";
import XMark from "./XMark";
import AddMeshSensors from "./AddMeshSensors";
import { animateCameraToPoint } from "../objectPlacement/hooks";
import { resources } from "../../redux/sensorAnalysisConstants";

const AddMesh = ({ orbitRef, existingSensors }) => {
  const { camera, raycaster, mouse } = useThree();
  const dispatch = useDispatch();
  const allWindows = useSelector((state) => state.windowData.allWindows);
  const { selectedPolygonYUp } = useWindowSelection();
  const selectedWindow = useMemo(
    () => selectedPolygonYUp,
    [selectedPolygonYUp]
  );

  const sensoranalysisState = useSelector(
    (state) => state.sensorAnalysis.sensorAnalysisState
  );
  const isSelectionActive = isResourceInState(
    sensoranalysisState,
    resources.SELECTABLE_WINDOWS
  );

  const selectedWindowsIds = useSelector(
    (state) => state.objectSelection.selections.windows
  );

  const [windowCoordSystem, setWindowCoordSystem] = useState(null);

  const [manualMeshSensors, setManualMeshSensors] =
    useManualMeshSensors(existingSensors);

  const selectedSensors = useSelector(
    (state) => state.sensorAnalysis.manualMeshing.state.selectedSensors
  );
  const setSelectedSensors = useCallback(
    (sensors) => dispatch(setManualMeshingSelectedSensors(sensors)),
    [dispatch]
  );

  const [isDragging, setIsDragging] = useState(false);
  const [dragStartPos, setDragStartPos] = useState(null);
  const [showXMark, setShowXMark] = useState(false);
  const [xMarkPosition, setXMarkPosition] = useState([0, 0, 0]);
  const [snapLines, setSnapLines] = useState({ horizontal: [], vertical: [] });
  const [hoveredSensor, setHoveredSensor] = useState(null);
  const [hoveredSnapHandle, setHoveredSnapHandle] = useState(null);

  const [isDraggingSnapLine, setIsDraggingSnapLine] = useState(false);
  const [draggedSnapLine, setDraggedSnapLine] = useState(null);

  const sensorOffset = useSelector(
    (state) => state.sensorAnalysis.manualMeshing.state.sensorOffset
  );
  const xMarkRef = useRef();

  const animateCameraToWindow = useCallback(
    (window) => {
      if (!window || !window.polygon) return;

      const center = getWindowCenter(window.polygon);

      animateCameraToPoint(
        center,
        camera,
        orbitRef,
        () => setShowXMark(true),
        DISTANCE_FROM_WINDOW_CENTER
      );
    },
    [camera, orbitRef]
  );

  const projectMouseToWindow = useCallback(
    (window) => {
      if (!window || !window.polygon || !windowCoordSystem) return null;

      raycaster.setFromCamera(mouse, camera);

      const plane = new THREE.Plane();
      plane.setFromNormalAndCoplanarPoint(
        windowCoordSystem.normal,
        windowCoordSystem.origin
      );

      const intersectionPoint = new THREE.Vector3();
      const intersected = raycaster.ray.intersectPlane(
        plane,
        intersectionPoint
      );

      if (intersected) {
        return intersectionPoint.toArray();
      }
    },
    [camera, mouse, raycaster, windowCoordSystem]
  );

  const handleWindowClick = useCallback(
    (window) => {
      setManualMeshSensors([]);
      setSelectedSensors([]);
      setShowXMark(false);

      const polygon = window.polygon;
      const coordSystem = getWindowLocalCoordSystem(polygon);

      setWindowCoordSystem(coordSystem);

      animateCameraToWindow(window);
    },
    [animateCameraToWindow, setSelectedSensors, setManualMeshSensors]
  );

  const handleCanvasClick = useCallback(
    (event) => {
      event.stopPropagation();
      if (!selectedWindow || !showXMark || !windowCoordSystem) return;

      const intersectionPoint = projectMouseToWindow(selectedWindow);
      if (!intersectionPoint) return;

      const polygon = selectedWindow.polygon;

      const snapResult = getSnapPosition(
        intersectionPoint,
        snapLines,
        windowCoordSystem
      );
      const finalPosition = snapResult.position;

      const newSensor = {
        id: "TEMP_ID" + Date.now() + Math.random(),
        position: finalPosition,
        windowId: selectedWindow.id,
      };

      setManualMeshSensors([...manualMeshSensors, newSensor]);

      const updatedSnapLines = calculateSnapLines(
        polygon,
        [...manualMeshSensors, newSensor],
        windowCoordSystem
      );
      setSnapLines(updatedSnapLines);

      setHoveredSensor(newSensor.id);
      setShowXMark(false);
    },
    [
      selectedWindow,
      showXMark,
      windowCoordSystem,
      projectMouseToWindow,
      snapLines,
      setManualMeshSensors,
      manualMeshSensors,
      setHoveredSensor,
      setShowXMark,
    ]
  );

  const handleSensorClick = useCallback(
    (sphere, event) => {
      const sensorId = sphere.id;
      event.stopPropagation();
      setSelectedSensors(selectedSensors?.includes(sensorId) ? [] : [sensorId]);
    },
    [setSelectedSensors, selectedSensors]
  );

  const handleSpheresDelete = useCallback(
    (sensors) => {
      setManualMeshSensors(
        manualMeshSensors.filter((s) => !sensors?.includes(s.id))
      );
      setSelectedSensors([]);
    },
    [setSelectedSensors, setManualMeshSensors, manualMeshSensors]
  );

  const handleSpheresDeleteEvent = useCallback(
    (event) => {
      handleSpheresDelete(event.detail);
    },
    [handleSpheresDelete]
  );

  useEventSubscription(windowEvents.SENSORS_DELETE, handleSpheresDeleteEvent);

  const handleMouseDown = useCallback(
    (event) => {
      if (!selectedWindow || event.button !== 0) return;

      const intersectionPoint = projectMouseToWindow(selectedWindow);
      if (intersectionPoint) {
        setIsDragging(true);
        setDragStartPos(intersectionPoint);
      }
    },
    [selectedWindow, projectMouseToWindow]
  );

  const moveSpheresOnSnapLine = useCallback(
    (lineType, oldValue, newValue) => {
      if (!windowCoordSystem) return;

      setManualMeshSensors(
        manualMeshSensors.map((sphere) => {
          const sphereWorld = new THREE.Vector3(...sphere.position);
          const sphereLocal = worldToWindowLocal(
            sphereWorld,
            windowCoordSystem
          );

          const isOnLine =
            lineType === "horizontal"
              ? Math.abs(sphereLocal.v - oldValue) < 0.05
              : Math.abs(sphereLocal.u - oldValue) < 0.05;

          if (isOnLine) {
            const newLocal =
              lineType === "horizontal"
                ? { u: sphereLocal.u, v: newValue }
                : { u: newValue, v: sphereLocal.v };

            const newWorldPos = windowLocalToWorld(
              newLocal.u,
              newLocal.v,
              windowCoordSystem
            );

            return {
              ...sphere,
              position: newWorldPos.toArray(),
            };
          }

          return sphere;
        })
      );
    },
    [windowCoordSystem, setManualMeshSensors, manualMeshSensors]
  );

  const checkIfMouseOutOfHorizontalBounds = useCallback(
    ({ snapLines, intersectionPoint }) => {
      const calculatedIntersectionPoint = intersectionPoint
        ? intersectionPoint
        : projectMouseToWindow(selectedWindow);
      if (!calculatedIntersectionPoint) return false;

      const calculatedSnapLines = snapLines
        ? snapLines
        : calculateSnapLines(
            selectedWindow.polygon,
            manualMeshSensors,
            windowCoordSystem
          );

      const bounds = calculatedSnapLines.bounds;
      const { minV, maxV } = bounds ? bounds : { minV: 0, maxV: 0 };

      const windowCoordSystemPoint = worldToWindowLocal(
        new THREE.Vector3(...calculatedIntersectionPoint),
        windowCoordSystem
      );

      if (windowCoordSystemPoint.v > maxV || windowCoordSystemPoint.v < minV) {
        return true;
      }
    },
    [selectedWindow, projectMouseToWindow, windowCoordSystem, manualMeshSensors]
  );

  const checkIfMouseOutOfVerticalBounds = useCallback(
    ({ snapLines, intersectionPoint }) => {
      const calculatedIntersectionPoint = intersectionPoint
        ? intersectionPoint
        : projectMouseToWindow(selectedWindow);
      if (!calculatedIntersectionPoint) return false;

      const calculatedSnapLines = snapLines
        ? snapLines
        : calculateSnapLines(
            selectedWindow.polygon,
            manualMeshSensors,
            windowCoordSystem
          );

      const bounds = calculatedSnapLines.bounds;
      const { minU, maxU } = bounds ? bounds : { minU: 0, maxU: 0 };

      const windowCoordSystemPoint = worldToWindowLocal(
        new THREE.Vector3(...calculatedIntersectionPoint),
        windowCoordSystem
      );

      if (windowCoordSystemPoint.u > maxU || windowCoordSystemPoint.u < minU) {
        return true;
      }
    },
    [selectedWindow, projectMouseToWindow, windowCoordSystem, manualMeshSensors]
  );

  const checkIfMouseOutOfBounds = useCallback(() => {
    const intersectionPoint = projectMouseToWindow(selectedWindow);
    if (!intersectionPoint) return false;

    const calculatedSnapLines = calculateSnapLines(
      selectedWindow.polygon,
      manualMeshSensors,
      windowCoordSystem
    );

    const props = {
      snapLines: calculatedSnapLines,
      intersectionPoint,
    };

    const isOutOfBoundsHorizontal = checkIfMouseOutOfHorizontalBounds(props);
    const isOutOfBoundsVertical = checkIfMouseOutOfVerticalBounds(props);

    if (isOutOfBoundsHorizontal || isOutOfBoundsVertical) {
      return true;
    }
  }, [
    selectedWindow,
    projectMouseToWindow,
    windowCoordSystem,
    manualMeshSensors,
    checkIfMouseOutOfHorizontalBounds,
    checkIfMouseOutOfVerticalBounds,
  ]);

  const checkIfSnapLineIsOutOfBounds = useCallback(
    (lineType) => {
      const props = { snapLines: null, intersectionPoint: null };

      if (lineType === "horizontal") {
        return checkIfMouseOutOfHorizontalBounds(props);
      } else {
        return checkIfMouseOutOfVerticalBounds(props);
      }
    },
    [checkIfMouseOutOfHorizontalBounds, checkIfMouseOutOfVerticalBounds]
  );

  const handleMouseMove = useCallback(
    (event) => {
      if (!selectedWindow) return;

      const currentPos = projectMouseToWindow(selectedWindow);
      const shouldManageSnapLineDrag =
        isDraggingSnapLine &&
        draggedSnapLine &&
        dragStartPos &&
        windowCoordSystem;

      if (shouldManageSnapLineDrag) {
        if (checkIfSnapLineIsOutOfBounds(draggedSnapLine.type)) return;
        if (currentPos) {
          const currentLocal = worldToWindowLocal(
            new THREE.Vector3(...currentPos),
            windowCoordSystem
          );

          const oldValue = draggedSnapLine.value;
          const newValue =
            draggedSnapLine.type === "horizontal"
              ? currentLocal.v
              : currentLocal.u;

          const isTooClose = isSnapTooClose(
            newValue,
            oldValue,
            draggedSnapLine.type,
            snapLines
          );
          if (isTooClose) return;

          moveSpheresOnSnapLine(draggedSnapLine.type, oldValue, newValue);

          setDraggedSnapLine((prev) => ({ ...prev, value: newValue }));
          setDragStartPos(currentPos);
        }
      } else {
        if (currentPos) {
          const bounds = snapLines.bounds;
          const { minU, maxU, minV, maxV } = bounds
            ? bounds
            : { minU: 0, maxU: 0, minV: 0, maxV: 0 };

          if (checkIfMouseOutOfBounds()) return;

          const verticalSnaplineHandlesVCoord =
            SNAP_HANDLE_OFFSET * maxV + (1 - SNAP_HANDLE_OFFSET) * minV;
          const horizontalSnaplineHandlesUcoord =
            SNAP_HANDLE_OFFSET * maxU + (1 - SNAP_HANDLE_OFFSET) * minU;

          const snapResult = getSnapPosition(
            currentPos,
            snapLines,
            windowCoordSystem
          );
          setXMarkPosition(snapResult.position);

          const isOnSpheres = manualMeshSensors?.some((sphere) =>
            sphere.position?.every(
              (pos, index) =>
                Math.abs(pos - snapResult.position[index]) < SPHERE_RADIUS
            )
          );

          for (let i = 0; i < manualMeshSensors.length; i++) {
            const sphere = manualMeshSensors[i];
            const isOnSphere = sphere.position?.every(
              (pos, index) =>
                Math.abs(pos - snapResult.position[index]) < SPHERE_RADIUS
            );
            if (isOnSphere) {
              setHoveredSensor(sphere.id);
            }
          }

          const isOnHorizontalSnapHandle = snapLines?.horizontal?.some(
            (line) => {
              const worldPoint = windowLocalToWorld(
                line?.localCoords?.u || 0,
                verticalSnaplineHandlesVCoord,
                windowCoordSystem
              );
              return Object.values(worldPoint)?.every(
                (pos, index) =>
                  Math.abs(pos - currentPos[index]) < SNAP_HANDLE_RADIUS
              );
            }
          );
          const isOnVerticalSnapHandle = snapLines?.vertical?.some((line) => {
            const worldPoint = windowLocalToWorld(
              horizontalSnaplineHandlesUcoord || 0,
              line?.localCoords?.v || 0,
              windowCoordSystem
            );
            return Object.values(worldPoint)?.every(
              (pos, index) =>
                Math.abs(pos - currentPos[index]) < SNAP_HANDLE_RADIUS
            );
          });

          const isOnSnapHandle =
            isOnHorizontalSnapHandle || isOnVerticalSnapHandle;
          setHoveredSnapHandle(isOnSnapHandle ? "line-handle" : null);

          if (!isOnSpheres) {
            setShowXMark(true);
            setHoveredSensor(null);
          }
        }
      }

      if (isDragging || isDraggingSnapLine) {
        setShowXMark(false);
        document.body.style.cursor = "grabbing";
      } else {
        document.body.style.cursor = "default";
        setShowXMark(true);
      }
    },
    [
      selectedWindow,
      windowCoordSystem,
      isDragging,
      checkIfSnapLineIsOutOfBounds,
      isDraggingSnapLine,
      draggedSnapLine,
      dragStartPos,
      projectMouseToWindow,
      snapLines,
      moveSpheresOnSnapLine,
      manualMeshSensors,
      checkIfMouseOutOfBounds,
      setHoveredSnapHandle,
    ]
  );

  const throttledHandleMouseMove = useMemo(
    () => debounce(handleMouseMove, DEBOUNCE_TIME, { immediate: true }),
    [handleMouseMove]
  );

  const handleMouseUp = useCallback(
    (event) => {
      setIsDragging(false);
      setIsDraggingSnapLine(false);
      setDraggedSnapLine(null);
      setDragStartPos(null);

      if (isDraggingSnapLine) {
        document.body.style.cursor = "default";
        if (orbitRef.current) {
          orbitRef.current.enabled = true;
        }
      }
    },
    [isDraggingSnapLine, orbitRef]
  );

  useEffect(() => {
    if (selectedWindow && windowCoordSystem) {
      const polygon = selectedWindow.polygon;

      const updatedSnapLines = calculateSnapLines(
        polygon,
        manualMeshSensors,
        windowCoordSystem
      );
      setSnapLines(updatedSnapLines);
    }
  }, [selectedWindow, manualMeshSensors, windowCoordSystem]);

  const handleSnapLineMouseDown = useCallback(
    (lineType, lineValue, event) => {
      event.stopPropagation();
      setIsDraggingSnapLine(true);
      setDraggedSnapLine({ type: lineType, value: lineValue });

      if (orbitRef.current) {
        orbitRef.current.enabled = false;
      }

      const intersectionPoint = projectMouseToWindow(selectedWindow);
      if (intersectionPoint) {
        setDragStartPos(intersectionPoint);
      }
    },
    [projectMouseToWindow, selectedWindow, orbitRef]
  );

  const handleCanvasClickWrapper = useCallback(
    (event) => {
      if (!isEventAClick(event)) return;
      if (isDraggingSnapLine) return;
      if (checkIfMouseOutOfBounds()) return;

      if (selectedWindow && showXMark && !hoveredSensor && !hoveredSnapHandle)
        handleCanvasClick(event);
    },
    [
      selectedWindow,
      checkIfMouseOutOfBounds,
      showXMark,
      handleCanvasClick,
      isDraggingSnapLine,
      hoveredSensor,
      hoveredSnapHandle,
    ]
  );

  const mirrorWindowsProps = [
    allWindows,
    selectedWindow,
    selectedWindowsIds,
    manualMeshSensors,
    windowCoordSystem,
  ];
  const mirrorWindowsNewSensors = useMemo(
    () => getAllMirrorWindowNewSensors(...mirrorWindowsProps),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    mirrorWindowsProps
  );

  const onPointerEnter = useCallback(
    (config) => {
      config.stopPropagation();
      const sensor = manualMeshSensors[config.instanceId];
      setHoveredSensor(sensor?.id);
      setShowXMark(false);
    },
    [setHoveredSensor, setShowXMark, manualMeshSensors]
  );

  const onPointerLeave = useCallback(
    (config) => {
      config.stopPropagation();
      setHoveredSensor(null);
      setShowXMark(true);
    },
    [setHoveredSensor, setShowXMark]
  );

  return (
    <>
      <group
        onClick={handleCanvasClickWrapper}
        onPointerMove={throttledHandleMouseMove}
        onPointerDown={handleMouseDown}
        onPointerUp={handleMouseUp}
      >
        <MeshWindow />
        {isSelectionActive && (
          <>
            <AddMeshSensors
              sensors={manualMeshSensors}
              windowCoordSystem={windowCoordSystem}
              hoveredSensor={hoveredSensor}
              selectedSensors={selectedSensors}
              handleSensorClick={handleSensorClick}
              allWindows={allWindows}
              sensorOffset={sensorOffset}
              onPointerEnter={onPointerEnter}
              onPointerLeave={onPointerLeave}
            />
            {mirrorWindowsNewSensors?.map((window) => (
              <AddMeshSensors
                sensors={window.sensors}
                windowCoordSystem={window.windowCoordSystem}
                hoveredSensor={hoveredSensor}
                selectedSensors={selectedSensors}
                handleSensorClick={handleSensorClick}
                allWindows={allWindows}
                sensorOffset={sensorOffset}
                key={window.id}
                onPointerEnter={onPointerEnter}
                onPointerLeave={onPointerLeave}
              />
            ))}
            <WindowClickDetector handleWindowClick={handleWindowClick} />

            {!(
              !showXMark ||
              !selectedWindow ||
              !windowCoordSystem ||
              hoveredSensor
            ) && (
              <XMark
                xMarkPosition={xMarkPosition}
                xMarkRef={xMarkRef}
                windowCoordSystem={windowCoordSystem}
              />
            )}

            {!(!selectedWindow || !windowCoordSystem || !snapLines.bounds) && (
              <SnapLines
                snapLines={snapLines}
                windowCoordSystem={windowCoordSystem}
                onPointerDown={handleSnapLineMouseDown}
                isDraggingSnapLine={isDraggingSnapLine}
              />
            )}
          </>
        )}
      </group>
    </>
  );
};

export default AddMesh;
